import { useMemo } from 'react';
import type { DatePickerProps } from 'antd/es/date-picker';
import { LazyAntDatePicker } from './lazyAntPicker';
import { PickerSuspense } from './PickerSuspense';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { cn } from 'app/lib/utils';

dayjs.extend(customParseFormat);

export interface DatePickerFieldProps extends Omit<
  DatePickerProps,
  'value' | 'onChange' | 'defaultValue'
> {
  /** Normalized calendar date `YYYY-MM-DD`, or empty string */
  value: string;
  onChange: (isoDate: string) => void;
}

/** `className` is merged onto the root `.ant-picker` node — target it directly, not `[&_.ant-picker]`. */
const defaultPickerChrome =
  'bg-white border border-solid border-gray-300 [&_.ant-picker-input>input]:bg-transparent';

/**
 * antd DatePicker façade: string `YYYY-MM-DD` in/out, default display `MM/DD/YYYY`.
 * Parses date-only values with {@link customParseFormat} to avoid implicit UTC shifts.
 *
 * Default chrome is a neutral white bordered input; pass `className` to override (e.g. tinted PDF overlay fields).
 */
export function DatePickerField({
  value,
  onChange,
  format = 'MM/DD/YYYY',
  className,
  size = 'small',
  getPopupContainer = () => document.body,
  allowClear = true,
  ...rest
}: DatePickerFieldProps) {
  const dayjsValue = useMemo(() => {
    const v = value?.trim();
    if (!v) return null;
    const d = dayjs(v, 'YYYY-MM-DD', true);
    return d.isValid() ? d : null;
  }, [value]);

  const handleChange: DatePickerProps['onChange'] = (date) => {
    onChange(date ? date.format('YYYY-MM-DD') : '');
  };

  return (
    <PickerSuspense size={size} style={rest.style}>
      <LazyAntDatePicker
        value={dayjsValue}
        onChange={handleChange}
        format={format}
        size={size}
        className={cn(defaultPickerChrome, className)}
        getPopupContainer={getPopupContainer}
        allowClear={allowClear}
        {...rest}
      />
    </PickerSuspense>
  );
}

export default DatePickerField;
